<template>
  <div class="car-tree-container">
    <div class="tree-header">
      <el-input
        v-model="searchKeyword"
        :placeholder="$t('carTree.searchPlaceholder')"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        @input="handleSearch"
      />
    </div>
    <el-tree
      ref="carTree"
      :data="treeData"
      node-key="id"
      :props="defaultProps"
      :highlight-current="true"
      :expand-on-click-node="true"
      :filter-node-method="filterNode"
      @node-click="handleNodeClick"
    >
      <span class="custom-tree-node" slot-scope="{ node, data }">
        <i :class="data.icon"></i>
        <span>{{ data.text }}</span>
        <span v-if="data.carCount" class="node-count">({{ data.carCount }})</span>
      </span>
    </el-tree>
  </div>
</template>

<script>
import { listParkingAreas } from '@/api/fee/carCreateFeeApi'
import { queryOwnerCars } from '@/api/simplify/simplifyCarFeeApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'CarTreeDiv',
  data() {
    return {
      treeData: [],
      defaultProps: {
        children: 'children',
        label: 'text'
      },
      communityId: '',
      searchKeyword: '',
      allCars: [],
      lastSelected: {},
      isInitialized: false
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.restoreLastSelected()
  },
  mounted() {
    this.initTree()
  },
  methods: {
    async initTree() {
      try {
        // 加载所有月租车辆（carTypeCd='1001'），不限制数量确保纯车牌车辆也能展示
        const carRes = await queryOwnerCars({
          page: 1,
          row: 5000,
          carTypeCd: '1001',
          communityId: this.communityId
        })
        this.allCars = (carRes.data && carRes.data.data) || []

        // 加载停车场列表
        const areaRes = await listParkingAreas({
          page: 1,
          row: 100,
          communityId: this.communityId
        })
        const areas = areaRes.parkingAreas || areaRes.data || []

        // 构建树：停车场 → 车辆
        this.buildTreeData(areas, this.allCars)

        // 恢复上次选择的车辆，或默认选择第一辆
        if (!this.isInitialized) {
          this.isInitialized = true
          this.$nextTick(() => {
            if (this.lastSelected && this.lastSelected.carId) {
              this.selectCarById(this.lastSelected.carId)
            } else {
              this.selectFirstCar()
            }
          })
        }
      } catch (error) {
        console.error('加载车辆树失败', error)
      }
    },

    buildTreeData(areas, cars) {
      const treeData = []
      const carByArea = {}

      // 按停车场分组车辆
      cars.forEach(car => {
        const areaName = car.areaNum || this.$t('carTree.noArea')
        if (!carByArea[areaName]) {
          carByArea[areaName] = []
        }
        carByArea[areaName].push(car)
      })

      // 有停车场的区域
      const assignedAreas = new Set()

      areas.forEach(area => {
        const areaName = area.num || area.paNum
        assignedAreas.add(areaName)
        const areaCars = carByArea[areaName] || []
        const children = areaCars.map(car => ({
          id: 'car_' + car.carId,
          carId: car.carId,
          carNum: car.carNum,
          ownerId: car.ownerId || '',
          roomId: car.roomId || '',
          ownerName: car.ownerName || '',
          psId: car.psId || '',
          num: car.num || '',
          text: car.carNum,
          icon: 'el-icon-truck'
        }))

        treeData.push({
          id: 'pa_' + (area.paId || areaName),
          text: areaName,
          icon: 'el-icon-location-outline',
          carCount: children.length,
          children: children
        })
      })

      // 未分配停车场的车辆
      Object.keys(carByArea).forEach(areaName => {
        if (!assignedAreas.has(areaName)) {
          const areaCars = carByArea[areaName]
          const children = areaCars.map(car => ({
            id: 'car_' + car.carId,
            carId: car.carId,
            carNum: car.carNum,
            ownerId: car.ownerId || '',
            roomId: car.roomId || '',
            ownerName: car.ownerName || '',
            text: car.carNum,
            icon: 'el-icon-truck'
          }))

          treeData.push({
            id: 'pa_other_' + areaName,
            text: areaName || this.$t('carTree.noArea'),
            icon: 'el-icon-location-outline',
            carCount: children.length,
            children: children
          })
        }
      })

      this.treeData = treeData
    },

    handleNodeClick(data) {
      if (data.id && data.id.startsWith('car_')) {
        const carData = {
          carId: data.carId,
          carNum: data.carNum,
          ownerId: data.ownerId,
          roomId: data.roomId,
          ownerName: data.ownerName
        }
        // 记住选择
        this.saveLastSelected(carData)
        this.$emit('selectCar', carData)
      }
    },

    handleSearch(val) {
      this.$refs.carTree.filter(val)
    },

    filterNode(value, data) {
      if (!value) return true
      return data.text && data.text.toLowerCase().includes(value.toLowerCase())
    },

    selectFirstCar() {
      // 使用 setTimeout 确保 el-tree 内部节点完全渲染
      setTimeout(() => {
        for (const area of this.treeData) {
          if (area.children && area.children.length > 0) {
            const firstCar = area.children[0]
            if (firstCar && firstCar.carId && this.$refs.carTree) {
              this.$refs.carTree.setCurrentKey(firstCar.id)
              const node = this.$refs.carTree.getNode(firstCar.id)
              if (node && node.parent) {
                node.parent.expanded = true
              }
              this.handleNodeClick(firstCar)
              return
            }
          }
        }
      }, 300)
    },

    selectCarById(carId) {
      const targetId = 'car_' + carId
      this.$nextTick(() => {
        this.$refs.carTree.setCurrentKey(targetId)
        // 找到节点并展开其父节点
        const node = this.$refs.carTree.getNode(targetId)
        if (node && node.parent) {
          node.parent.expanded = true
        }
      })
    },

    saveLastSelected(carData) {
      try {
        localStorage.setItem('carTreeLastSelected', JSON.stringify(carData))
      } catch (e) {
        // ignore
      }
    },

    restoreLastSelected() {
      try {
        const saved = localStorage.getItem('carTreeLastSelected')
        if (saved) {
          this.lastSelected = JSON.parse(saved)
        }
      } catch (e) {
        // ignore
      }
    },

    clearLastSelected() {
      localStorage.removeItem('carTreeLastSelected')
      this.lastSelected = {}
    },

    refresh() {
      this.initTree()
    },

    open() {
      this.initTree()
    }
  }
}
</script>

<style scoped>
.car-tree-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tree-header {
  padding: 8px;
  border-bottom: 1px solid #e4e7ed;
}

.tree-header .el-input {
  width: 100%;
}

.el-tree {
  flex: 1;
  overflow-y: auto;
  padding: 5px;
}

.custom-tree-node {
  font-size: 13px;
  flex: 1;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.custom-tree-node i {
  margin-right: 4px;
  color: #909399;
  font-size: 14px;
}

.node-count {
  color: #909399;
  font-size: 11px;
  margin-left: 4px;
}
</style>
